import {
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  type PopoverProps,
} from "react-aria-components";
import { cn } from "@core/utils";
import { Icon, type IconKey } from "@core/assets";
import { Button } from "./Button";

export interface DropdownMenuAction {
  id: string;
  label: string;
  icon: IconKey;
  onAction: () => void;
  isDisabled?: boolean;
  /** `danger`: red, for a destructive action (« Supprimer »). */
  tone?: "default" | "danger";
}

interface DropdownMenuProps {
  items: DropdownMenuAction[];
  /** The trigger has no visible label of its own (an icon-only "…" button). */
  "aria-label": string;
  placement?: PopoverProps["placement"];
}

/**
 * The card-level "…" overflow menu (Figma 05.2 · 468:23358 — a generic pattern reused here for
 * round cards). React Aria's `MenuTrigger`/`Menu`/`MenuItem` own focus management, positioning and
 * outside-click/Escape dismissal; only the visual layer is ours.
 *
 * Disabled items are RENDERED, not omitted — same convention as everywhere else in the app
 * (`RoundCard`'s own actions) — so an administrator sees the action exists rather than wondering
 * why it's missing. Figma pairs a disabled item with a dark tooltip explaining why; that tooltip
 * is not wired here yet since no current caller has a disabled item — add it via `@core/components`
 * `Tooltip` when one does, rather than guessing at the copy now.
 */
export function DropdownMenu({
  items,
  "aria-label": ariaLabel,
  placement = "bottom end",
}: DropdownMenuProps) {
  return (
    <MenuTrigger>
      <Button
        variant="tertiary"
        aria-label={ariaLabel}
        className="p-2 text-(--text-tertiary)"
      >
        <Icon name="dots-horizontal" size={20} />
      </Button>

      <Popover
        placement={placement}
        className="entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95"
      >
        <Menu
          className="w-64 overflow-auto rounded-md border border-(--border-default) bg-surface-card py-1.5 shadow-lg outline-none"
          onAction={(key) => {
            items.find((item) => item.id === key)?.onAction();
          }}
        >
          {items.map((item) => (
            <MenuItem
              key={item.id}
              id={item.id}
              textValue={item.label}
              isDisabled={item.isDisabled}
              className={cn(
                "flex cursor-pointer items-center gap-sm px-md py-3",
                "text-sm-regular outline-none",
                item.tone === "danger"
                  ? "text-(--text-error)"
                  : "text-(--text-secondary)",
                "focus:bg-gray-50",
                "disabled:cursor-not-allowed disabled:opacity-40"
              )}
            >
              <Icon name={item.icon} size={16} className="shrink-0" />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
